import axios from "axios";
import { useEffect, useState } from "react";
import { BASE_URL } from "../../../../config/api";
import { AlertTriangle, CheckCircle, CloudUpload, FileText, Loader2, Trash2, X, XCircle } from "lucide-react";

export default function UploadFileModal() {
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);
  const [confirmStatusId, setConfirmStatusId] = useState(null);

  const [loadingImages, setLoadingImages] = useState(false);

  // --- NEW STATES FOR STAGING & TAGGING ---
  const [stagedFiles, setStagedFiles] = useState([]); // [{ file, tag, preview }]
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const companyId = authUser?.company_id || authUser?.id;

  const tagColors = {
    payment: "bg-green-100 text-green-700",
    invoice: "bg-blue-100 text-blue-700",
    warranty: "bg-purple-100 text-purple-700",
    claim: "bg-orange-100 text-orange-700",
    default: "bg-slate-100 text-slate-600",
  };

  const fetchImages = async () => {

    try {

      setLoadingImages(
        true
      );

      const res =
        await axios.get(
          `${BASE_URL}warranty/template-images/${companyId}`
        );

      if (
        res.data?.status
      ) {

        setImages(
          res.data.data
        );
      }

    } catch (err) {

      console.error(
        "Error fetching images:",
        err
      );

    } finally {

      setLoadingImages(
        false
      );

    }
  };

  useEffect(() => {
  fetchImages();
  setStagedFiles([]);
}, []);

  // Handle file selection (Staging)
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const newStaged = selectedFiles.map((file) => ({
      file,
      tag: "payment", // default tag
      preview: URL.createObjectURL(file),
    }));

    setStagedFiles((prev) => [...prev, ...newStaged]);
    e.target.value = null; // Reset input
  };

  const updateStagedTag = (index, newTag) => {
    const updated = [...stagedFiles];
    updated[index].tag = newTag;
    setStagedFiles(updated);
  };

  const removeStagedFile = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Final Upload Process
  const processUpload = async () => {

    setIsUploading(
      true
    );

    for (const item of stagedFiles) {

      const formData =
        new FormData();

      formData.append(
        "file",
        item.file
      );

      formData.append(
        "tag",
        item.tag
      );

      try {

        const uploadResponse =
          await axios.post(
            `${BASE_URL}warranty/upload-file`,
            formData,
            {
              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        const uploadData =
          uploadResponse.data;

        const imageUrl =
          uploadData?.data
            ?.file_url;

        if (
          imageUrl
        ) {

          await axios.post(
            `${BASE_URL}warranty/template-images`,
            {
              link:
                imageUrl,

              tag:
                item.tag,

              company_id:
                companyId,
            },
            {
              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "application/json",
              },
            }
          );
        }

      } catch (err) {

        console.error(
          "Upload failed:",
          err
        );
      }
    }

    setIsUploading(
      false
    );

    setShowUploadConfirm(
      false
    );

    setStagedFiles(
      []
    );

    fetchImages();
  };

  const handleStatusToggle = async (id) => {
    try {
      setIsUpdatingStatus(id);
      const currentImage = images.find(img => img.id === id);
      const newStatus = currentImage.status == 1 ? 0 : 1;
      const res = await axios.patch(`${BASE_URL}warranty/template-images/status/${id}`, { status: newStatus });
      if (res.data?.status) fetchImages();
    } catch (err) {
      console.error("Status update failed:", err);
    } finally {
      setIsUpdatingStatus(null);
      setConfirmStatusId(null);
    }
  };

  const processDelete = async (id) => {
    try {
      setIsDeleting(id);
      const res = await axios.delete(`${BASE_URL}warranty/template-images/${id}`);
      if (res.data?.status) fetchImages();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(null);
      setConfirmDeleteId(null);
    }
  };


  const TableSkeleton = () => {
  return (
    <>
      {[...Array(4)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-4 py-3">
            <div className="h-4 w-6 bg-slate-200 rounded"></div>
          </td>
          <td className="px-4 py-3">
            <div className="w-10 h-10 bg-slate-200 rounded"></div>
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-20 bg-slate-200 rounded"></div>
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-16 bg-slate-200 rounded"></div>
          </td>
          <td className="px-4 py-3">
            <div className="h-4 w-24 bg-slate-200 rounded"></div>
          </td>
          <td className="px-4 py-3">
            <div className="flex gap-2 justify-center">
              <div className="w-6 h-6 bg-slate-200 rounded"></div>
              <div className="w-6 h-6 bg-slate-200 rounded"></div>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
};


  return (
    <div className="w-full p-6 bg-white rounded-2xl shadow-md">
      {/* <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden relative"> */}
        
        {/* NEW: UPLOAD CONFIRMATION OVERLAY */}
        {showUploadConfirm && (
          <div className="absolute inset-0 z-[80] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full border border-slate-100 animate-in fade-in zoom-in duration-200">
              <div className="flex flex-col items-center text-center">
                <div className="bg-blue-50 p-3 rounded-full text-blue-600 mb-4"><CloudUpload size={28} /></div>
                <h3 className="font-bold text-lg text-slate-800 mb-1">Confirm Upload?</h3>
                <p className="text-sm text-slate-500 mb-6">You are about to upload {stagedFiles.length} images to the server.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowUploadConfirm(false)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-semibold">Cancel</button>
                <button onClick={processUpload} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center">
                  {isUploading ? <Loader2 className="animate-spin" size={18} /> : "Yes, Upload"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STATUS TOGGLE CONFIRMATION */}
        {confirmStatusId && (
          <div className="absolute inset-0 z-[70] bg-slate-900/10 backdrop-blur-[2px] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full border border-slate-100">
              <h3 className="font-bold text-lg text-slate-800 mb-2 text-center">Update Visibility?</h3>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setConfirmStatusId(null)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-semibold">Cancel</button>
                <button onClick={() => handleStatusToggle(confirmStatusId)} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center">
                  {isUpdatingStatus ? <Loader2 className="animate-spin" size={18} /> : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE CONFIRMATION */}
        {confirmDeleteId && (
          <div className="absolute inset-0 z-[60] bg-slate-900/20 backdrop-blur-[2px] flex items-center justify-center p-4">
             <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full border border-slate-100">
               <div className="flex flex-col items-center text-center">
                 <div className="bg-red-50 p-3 rounded-full text-red-600 mb-4"><AlertTriangle size={28} /></div>
                 <h3 className="font-bold text-lg text-slate-800 mb-1">Delete Image?</h3>
               </div>
               <div className="flex gap-3 mt-6">
                 <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-semibold">Cancel</button>
                 <button onClick={() => processDelete(confirmDeleteId)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold flex items-center justify-center">
                   {isDeleting ? <Loader2 className="animate-spin" size={18} /> : "Yes, Delete"}
                 </button>
               </div>
             </div>
          </div>
        )}

        {/* HEADER */}
        <div className="flex justify-between items-center p-6 border-b bg-white">
          <h2 className="text-xl font-bold text-slate-800">Template Manager</h2>
          {/* <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full transition-colors"><X size={20} className="text-slate-500" /></button> */}
        </div>

        {/* CONTENT */}
        <div className="p-6 overflow-y-auto space-y-6">
          
          {/* DRAG & DROP ZONE */}
          <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleFileChange({ target: { files: e.dataTransfer.files } }); }}>
            <label className="relative border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:bg-slate-50 transition-all flex flex-col items-center justify-center">
              <FileText className="text-blue-500 w-10 h-10 mb-2" />
              <p className="text-sm font-semibold text-slate-700">Click or Drag images to stage for upload</p>
              <input type="file" multiple className="hidden" onChange={handleFileChange} accept="image/*" />
            </label>
          </div>

          {/* STAGED FILES LIST WITH TAG INPUT */}
          {stagedFiles.length > 0 && (
            <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold text-blue-800">Ready to Upload ({stagedFiles.length})</h3>
                <div className="flex gap-2">
                    <button onClick={() => setStagedFiles([])} className="text-xs font-semibold text-slate-500 hover:text-red-500">Clear All</button>
                    <button onClick={() => setShowUploadConfirm(true)} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-700 transition-shadow shadow-md">Upload All</button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {stagedFiles.map((item, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg border border-blue-100 flex items-center gap-3 shadow-sm">
                    <img src={item.preview} className="w-12 h-12 rounded-lg object-cover border border-slate-100" />
                    <div className="flex-1">
                      <input 
                        type="text" 
                        value={item.tag} 
                        onChange={(e) => updateStagedTag(idx, e.target.value)}
                        placeholder="Enter tag..."
                        className="w-full text-xs p-2 border border-slate-200 rounded-md focus:ring-1 focus:ring-blue-400 outline-none"
                      />
                    </div>
                    <button onClick={() => removeStagedFile(idx)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* EXISTING IMAGES TABLE */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">S.No</th>
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Image</th>
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Tag</th>
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingImages ? (
                  <TableSkeleton />
                ) : images.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-slate-400 text-sm">
                      No template images found
                    </td>
                  </tr>
                ) : (
                  images.map((img, index) => (
                    <tr key={img.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-slate-600">{index + 1}</td>

                      <td className="px-4 py-3">
                        <div className="w-10 h-10 rounded border border-slate-200 overflow-hidden bg-slate-100 cursor-pointer">
                          <img
                            src={img.link}
                            className="w-full h-full object-cover"
                            onClick={() => window.open(img.link, "_blank")}
                          />
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${
                            tagColors[img.tag?.toLowerCase()] || tagColors.default
                          }`}
                        >
                          {img.tag}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            img.status == 1
                              ? "bg-green-100 text-green-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {img.status == 1 ? "ACTIVE" : "HIDDEN"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-xs text-slate-400">
                        {new Date(img.created_at).toLocaleDateString("en-GB")}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setConfirmStatusId(img.id)}
                            className={`p-1.5 rounded-md ${
                              img.status == 1
                                ? "text-amber-500 hover:bg-amber-50"
                                : "text-blue-500 hover:bg-blue-50"
                            }`}
                          >
                            {img.status == 1 ? (
                              <XCircle size={16} />
                            ) : (
                              <CheckCircle size={16} />
                            )}
                          </button>

                          <button
                            onClick={() => setConfirmDeleteId(img.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

          {/* <div className="p-4 bg-slate-50 border-t flex justify-end">
            <button onClick={onClose} className="px-6 py-2 bg-blue-700 text-white rounded-lg text-sm font-semibold hover:bg-black transition-colors">Close</button>
          </div> */}
      {/* </div> */}
    </div>
  );
}